import { useEffect, useState } from "react";
import axios from "axios";
import { Users, CalendarClock } from "lucide-react";
import { PROGRAMMES, PROGRAMME_COLORS, termChipClass } from "@/lib/format";
import { isoDate } from "@/lib/dates";

const slug = (s) => s.replace(/\s+/g, "-").toLowerCase();
const isTrip = (name) => /bus|van|trip|excursion|off-?site/i.test(name || "");

export function currentTerm(terms, termList) {
  const today = isoDate();
  const ordered = [...(terms || [])].sort((a, b) => a.term - b.term);
  for (let i = 0; i < ordered.length; i++) {
    const t = ordered[i];
    if (today >= t.start && today <= t.end) return `Term ${t.term}`;
    if (today > t.end && ordered[i + 1] && today < ordered[i + 1].start) return `Term ${t.term} Holidays`;
  }
  if (!ordered.length) return termList[0];
  return today < ordered[0].start ? "Summer Holidays (Jan)" : "Summer Holidays (Dec)";
}

export default function RosterPlanner({ rows, terms, settings, api, year, derived = false }) {
  const [shares, setShares] = useState({});
  const [term, setTerm] = useState("");
  const [todayActual, setTodayActual] = useState(null);
  useEffect(() => {
    if (!term && terms.length) setTerm(currentTerm(settings?.termsForYear, terms));
  }, [terms, settings, term]);
  useEffect(() => {
    axios.get(`${api}/programmes`, { params: { year } }).then((r) => {
      const m = {};
      (r.data.sessionShare || []).forEach((ps) => { const last = ps.terms[ps.terms.length - 1]; if (last) m[ps.programme] = Object.fromEntries(last.sessions.map((s) => [s.sessionId, s.share])); });
      setShares(m);
    }).catch(() => {});
  }, [api, year]);
  // Sanity-check the forecast against what's actually confirmed today, so planning a future week's
  // roster doesn't happen in total isolation from the live booking numbers on Bookings/Dashboard.
  useEffect(() => { axios.get(`${api}/bookings/today-summary`).then((r) => setTodayActual(r.data)).catch(() => setTodayActual(null)); }, [api]);
  const actualFor = (programme) => todayActual?.programmes.find((p) => p.programme === programme)?.total;

  const ratios = settings?.ratios || {};
  const plans = PROGRAMMES.map((p) => {
    const r = rows[`${term}|${p}`];
    const avg = parseFloat(r?.avgAttendance) || 0;
    if (!avg) return null;
    const ratio = ratios[p] || 10, offRatio = ratios.Offsite || 8;
    const sessions = settings?.feeSchedule?.[p] || [];
    const share = shares[p];
    const perSession = sessions.map((s) => {
      const sh = share && Object.keys(share).length ? (share[s.id] || 0) : 1 / (sessions.length || 1);
      const kids = Math.round(avg * sh);
      const rt = isTrip(s.name) ? offRatio : ratio;
      return { id: s.id, name: s.name, kids, ratio: rt, staff: kids ? Math.ceil(kids / rt) : 0, trip: isTrip(s.name) };
    }).filter((x) => x.kids > 0);
    return { programme: p, avg, ratio, peakStaff: Math.ceil(avg / ratio), days: parseFloat(r?.days) || 0, perSession, hasShare: !!(share && Object.keys(share).length) };
  }).filter(Boolean);

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] overflow-hidden" data-testid="roster-planner">
      <div className="px-6 py-4 border-b border-[var(--hp-border)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[var(--hp-c-cyan)]" /><div><h3 className="font-head text-lg font-semibold text-[var(--hp-heading)]">Staff Roster Planner</h3><p className="text-xs text-[var(--hp-muted)]">Staff needed per day next week from forecast attendance and your licensing ratios (trips use the off-site ratio){derived ? " · from your saved monthly forecast, split across each term by its days" : ""}</p></div></div>
        <div className="flex flex-wrap gap-1.5">
          {terms.map((t) => (
            <button key={t} data-testid={`roster-term-${slug(t)}`} onClick={() => setTerm(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${term === t ? "bg-[var(--hp-tint-10)] text-[var(--hp-heading)] ring-1 ring-[var(--hp-border-hi)]" : "text-[var(--hp-muted)] hover:text-[var(--hp-heading)]"}`}>{t}</button>
          ))}
        </div>
      </div>
      {plans.length === 0 ? (
        <p className="px-6 py-8 text-center text-sm text-[var(--hp-muted)]" data-testid="roster-empty">No forecast attendance for {term || "this term"} yet — enter Avg kids/day in the editor above (unsaved values count too).</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-6">
          {plans.map((pl) => (
            <div key={pl.programme} className="rounded-xl border border-[var(--hp-border)] overflow-hidden" data-testid={`roster-${slug(pl.programme)}`}>
              <div className="px-4 py-2.5 bg-[var(--hp-bg)]/40 border-b border-[var(--hp-border)] flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--hp-heading)]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PROGRAMME_COLORS[pl.programme] }} />{pl.programme} <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${termChipClass(term)}`}>{term}</span></span>
                <span className="text-xs text-[var(--hp-muted)]">{pl.avg} kids/day · ratio 1:{pl.ratio}{actualFor(pl.programme) != null && <span data-testid={`roster-actual-today-${slug(pl.programme)}`} title="Actually confirmed today, for comparison — see Bookings for the live roll"> · today: {actualFor(pl.programme)} confirmed</span>}</span>
              </div>
              <div className="px-4 py-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#22D3EE]/15 text-[var(--hp-c-cyan)]"><Users className="h-5 w-5" /></div>
                  <div><p className="text-xs uppercase tracking-wider text-[var(--hp-muted)]">Peak staff needed per day</p><p data-testid={`roster-peak-${slug(pl.programme)}`} className="font-mono-num text-2xl font-bold text-[var(--hp-heading)]">{pl.peakStaff}<span className="text-sm text-[var(--hp-muted)] font-normal"> staff for {Math.round(pl.avg)} kids</span></p></div>
                </div>
                {pl.perSession.length > 0 && (
                  <table className="w-full text-xs">
                    <thead><tr className="text-xs uppercase tracking-wider text-[var(--hp-muted)] border-b border-[var(--hp-border)]"><th className="py-1.5 text-left font-semibold">Session</th><th className="py-1.5 text-right font-semibold">Est. kids</th><th className="py-1.5 text-right font-semibold">Ratio</th><th className="py-1.5 text-right font-semibold">Staff</th></tr></thead>
                    <tbody>
                      {pl.perSession.map((s) => (
                        <tr key={s.id} className="border-b border-[var(--hp-border)]/40" data-testid={`roster-session-${s.id}`}>
                          <td className="py-1.5 text-[var(--hp-heading)]">{s.name}{s.trip && <span className="ml-1.5 rounded-full bg-[#F472B6]/15 px-1.5 py-0.5 text-xs font-semibold text-[var(--hp-c-pink)]">off-site</span>}</td>
                          <td className="py-1.5 text-right font-mono-num">{s.kids}</td>
                          <td className="py-1.5 text-right font-mono-num text-[var(--hp-muted)]">1:{s.ratio}</td>
                          <td className="py-1.5 text-right font-mono-num font-semibold text-[var(--hp-c-cyan)]">{s.staff}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p className="text-xs text-[var(--hp-muted)] mt-2">{pl.hasShare ? "Session split based on your most recent term's session mix." : "No session history yet — kids split evenly across sessions."} Sessions overlap at the start, so roster for the peak figure.</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
